import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { closeHTMLDrawer } from "../../state/slices/htmlDrawerSlice";

const FORM_STYLE_REPORTS = new Set([
  "credit_notes",
  "credit_notes_view",
  "debit_notes",
  "debit_notes_view",
  "parts_request_view",
  "parts_issue_view",
  "parts_return_view",
  "invoice_view",
  "sales_return_view",
]);

const REPORT_TITLE_LABELS: Record<string, string> = {
  credit_notes: "Credit note",
  credit_notes_view: "Credit note",
  debit_notes: "Debit note",
  debit_notes_view: "Debit note",
  parts_request_view: "Parts request",
  parts_issue_view: "Parts issue",
  parts_return_view: "Parts return",
  invoice_view: "Invoice",
  sales_return_view: "Sales return voucher",
};

const isFormStyleReport = (reportName: string | null): boolean =>
  !!reportName && FORM_STYLE_REPORTS.has(reportName);

const formatReportTitle = (reportName: string | null): string => {
  if (!reportName) return "Report";
  if (REPORT_TITLE_LABELS[reportName]) {
    return REPORT_TITLE_LABELS[reportName];
  }
  return reportName
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const HTMLDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isOpen, htmlContent, reportName, loading, error } = useAppSelector(
    (state) => state.htmlDrawer
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const formStyle = isFormStyleReport(reportName);
  const displayTitle = formatReportTitle(reportName);

  const handleClose = () => {
    dispatch(closeHTMLDrawer());
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const INJECTED_WRAPPER_CLASS = "html-drawer-injected-wrapper";

  const buildInjectedStyles = (useFormStyle: boolean) => {
    const base = `
      .${INJECTED_WRAPPER_CLASS}, .${INJECTED_WRAPPER_CLASS} * { box-sizing: border-box; }
      .${INJECTED_WRAPPER_CLASS} > * {
        height: auto !important;
        min-height: auto !important;
        max-height: none !important;
        overflow: visible !important;
      }`;

    if (!useFormStyle) {
      return `<style>${base}</style>`;
    }

    return `<style>${base}
      .${INJECTED_WRAPPER_CLASS},
      .${INJECTED_WRAPPER_CLASS} .page-card,
      .${INJECTED_WRAPPER_CLASS} .main_block,
      .${INJECTED_WRAPPER_CLASS} .main_block.table {
        max-width: none !important;
        width: 100% !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
      }
      .${INJECTED_WRAPPER_CLASS} .screen-title {
        display: none !important;
      }
      .${INJECTED_WRAPPER_CLASS} .screen-root {
        min-height: 100% !important;
        padding: 0 !important;
      }
    </style>`;
  };

  useEffect(() => {
    if (isOpen && htmlContent && contentRef.current) {
      contentRef.current.innerHTML = "";

      let contentToInject: string;

      if (htmlContent.includes("<!DOCTYPE") || htmlContent.includes("<html")) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, "text/html");
        const headStyles = doc.querySelectorAll("head style");
        let styleContent = "";
        headStyles.forEach((style) => {
          styleContent += style.outerHTML;
        });
        const bodyContent = doc.body ? doc.body.innerHTML : htmlContent;
        contentToInject = styleContent + bodyContent;
      } else {
        contentToInject = htmlContent;
      }

      contentRef.current.innerHTML =
        buildInjectedStyles(formStyle) +
        `<div class="${INJECTED_WRAPPER_CLASS}" style="display: block; min-height: 0; width: 100%;">${contentToInject}</div>`;
    }
  }, [isOpen, htmlContent, formStyle]);

  if (!isOpen) {
    return null;
  }

  const drawerWidthClass = formStyle ? "w-full sm:w-[90vw] top-[4rem]" : "w-full max-w-5xl";

  const drawerBgClass = formStyle
    ? "bg-gray-100 dark:bg-gray-900"
    : "bg-white dark:bg-gray-800";

  const contentBgClass = formStyle
    ? "bg-gray-100 dark:bg-gray-900"
    : "bg-white dark:bg-gray-800";

  const contentStyle = formStyle
    ? {
        boxSizing: "border-box" as const,
        backgroundColor: "#f3f4f6",
        padding: "4px 0 16px",
        isolation: "isolate" as const,
        colorScheme: "light" as const,
        WebkitOverflowScrolling: "touch" as const,
      }
    : {
        boxSizing: "border-box" as const,
        backgroundColor: "#ffffff",
        padding: "16px 20px",
        isolation: "isolate" as const,
        colorScheme: "light" as const,
        WebkitOverflowScrolling: "touch" as const,
      };

  return (
    <>
      <div
        className="fixed inset-0 !z-[100050] bg-black bg-opacity-50 transition-opacity duration-300"
        onClick={handleClose}
        aria-hidden="true"
      />

      <div
        className={`fixed right-0 top-0 bottom-0 !z-[100051] ${drawerWidthClass} ${drawerBgClass} shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col`}
        style={{
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
          height: "100vh",
          maxHeight: "100dvh",
        }}
      >
        {formStyle ? (
          <div className="flex items-center gap-3 px-4 py-3 shrink-0 bg-gray-100 dark:bg-gray-900">
            <button
              type="button"
              onClick={handleClose}
              className="flex items-center justify-center w-9 h-9 bg-white dark:!bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 active:scale-95 shadow-sm"
              aria-label="Go back"
              title="Go back"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-gray-700 dark:text-gray-200"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 m-0 tracking-tight">
              {displayTitle}
            </h1>
          </div>
        ) : (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 shrink-0">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                {""}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
              aria-label="Close drawer"
            >
              <svg
                className="w-6 h-6 text-gray-600 dark:text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        <div
          className="relative flex-1 min-h-0"
          style={formStyle ? undefined : { height: "calc(100vh - 4.5rem)", minHeight: 0 }}
        >
          {loading && (
            <div className={`absolute inset-0 flex items-center justify-center ${contentBgClass}`}>
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-600 dark:text-gray-300">Loading content...</p>
              </div>
            </div>
          )}

          {error && (
            <div className={`absolute inset-0 flex items-center justify-center ${contentBgClass} p-6 overflow-auto`}>
              <div className="text-center">
                <svg
                  className="w-16 h-16 text-red-500 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  Error Loading Content
                </p>
                <p className="text-gray-600 dark:text-gray-400">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && htmlContent && (
            <div
              ref={contentRef}
              className="html-drawer-content-wrapper h-full w-full overflow-auto"
              style={contentStyle}
            />
          )}

          {!loading && !error && !htmlContent && (
            <div className={`absolute inset-0 flex items-center justify-center ${contentBgClass}`}>
              <p className="text-gray-500 dark:text-gray-400">No content to display</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
